import { Component, inject } from '@angular/core';
import { JsonPipe, KeyValuePipe } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { AuthService } from '../../core/auth/auth.service';
import { PermissionsService } from '../../core/services/permissions.service';

@Component({
    selector: 'app-profile',
    standalone: true,
    imports: [CardModule, TagModule, JsonPipe, KeyValuePipe],
    template: `
        <p-card header="User Profile">
            @if (userData(); as u) {
                <div class="flex items-center gap-3 mb-4">
                    <span
                        class="flex items-center justify-center rounded-full bg-primary text-primary-contrast font-semibold"
                        style="width:3rem;height:3rem"
                    >{{ initials() }}</span>
                    <div>
                        <div class="text-lg font-semibold">{{ u['email'] ?? u['cognito:username'] ?? u['username'] ?? 'Signed in' }}</div>
                        <div class="text-sm text-surface-500 dark:text-surface-400">{{ u['sub'] }}</div>
                    </div>
                </div>

                <div class="mb-4">
                    <span class="font-medium mr-2">Roles:</span>
                    @if (roles().length) {
                        @for (r of roles(); track r) {
                            <p-tag [value]="r" severity="info" class="mr-1" />
                        }
                    } @else {
                        <span class="text-surface-500">none assigned</span>
                    }
                </div>

                <div class="mb-4">
                    <span class="font-medium mr-2">Permissions:</span>
                    @if (permissions().length) {
                        @for (p of permissions(); track p) {
                            <p-tag [value]="p" severity="success" class="mr-1 mb-1" />
                        }
                    } @else {
                        <span class="text-surface-500">none</span>
                    }
                </div>

                <div class="flex flex-col gap-2">
                    @for (claim of u | keyvalue; track claim.key) {
                        <div class="flex gap-3">
                            <span class="font-medium w-56 text-surface-600 dark:text-surface-300">{{ claim.key }}</span>
                            <span class="break-all">{{ claim.value }}</span>
                        </div>
                    }
                </div>

                <details class="mt-4" open>
                    <summary class="cursor-pointer text-sm text-surface-500">ID token claims (incl. cognito:groups)</summary>
                    <pre class="text-sm mt-2 overflow-auto">{{ u | json }}</pre>
                </details>
            } @else {
                <p class="m-0">Not signed in. Use <b>Login</b> in the top bar.</p>
            }
        </p-card>
    `
})
export class Profile {
    private readonly auth = inject(AuthService);
    private readonly perms = inject(PermissionsService);
    readonly roles = this.perms.roles;
    readonly permissions = this.perms.permissions;

    // ID-token claims are the one identity source now — they carry email, cognito:username AND
    // cognito:groups (the userinfo endpoint went away with the hosted flow).
    readonly userData = this.auth.claims;

    initials(): string {
        const u = this.userData() as Record<string, string> | null;
        const source: string = u?.['email'] ?? u?.['cognito:username'] ?? u?.['username'] ?? u?.['sub'] ?? '?';
        return source.substring(0, 2).toUpperCase();
    }
}
